import React, { useReducer } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  Typography,
  Dialog,
  DialogActions,
  Button,
  IconButton
} from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';

import { isNullOrUndefined } from '../utilities.js';
import { FONT_WEIGHT_700 } from '../constants.js';

const ACTION_TYPES = {
  SHOW_INFO: 'SHOW_INFO',
  HIDE_INFO: 'HIDE_INFO'
};

const ACTIONS = {
  showInfo: () => ({ type: ACTION_TYPES.SHOW_INFO }),
  hideInfo: () => ({ type: ACTION_TYPES.HIDE_INFO })
};

const reducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.SHOW_INFO: {
      return {
        ...state,
        isInfoDialogVisible: true
      };
    }

    case ACTION_TYPES.HIDE_INFO: {
      return {
        ...state,
        isInfoDialogVisible: false
      };
    }

    default: {
      throw Error('Indicator dialog reducer default case');
    }
  }
};

const initialState = {
  isInfoDialogVisible: false
};

const CARD_CONTENT_PADDING_PX = 4;

function EmptyIndicatorContent(props) {
  return (
    <>
      <Typography variant="body2" color="text.primary">
            {props.descriptionText}
      </Typography>

      <Typography
        variant="body"
        sx={{
          marginTop: '8px',
          marginBottom: '8px',
          fontWeight: FONT_WEIGHT_700
        }}
      >
        Not enough data.
      </Typography>
    </>
  );
}

function IndicatorContent(props) {
  return (
    <>
      <Typography variant="body2" color="text.primary">
        {props.descriptionText}
      </Typography>

      <Typography
        variant="body"
        sx={{
          marginTop: '8px',
          marginBottom: '8px',
          fontWeight: FONT_WEIGHT_700
        }}
      >
        {props.metric}
      </Typography>

      <Typography variant="body2" color="text.secondary">
        {props.footerText}
      </Typography>
    </>
  );
}

function Indicator(props) {
  const { descriptionText, metric, footerText, infoText } = props;

  const [infoState, infoDispatch] = useReducer(reducer, initialState);

  const handleShowInfo = () => infoDispatch(ACTIONS.showInfo());
  const handleHideInfo = () => infoDispatch(ACTIONS.hideInfo());

  const isMissingMetric = (
    isNullOrUndefined(metric) || metric === '' || metric.length === 0
  );

  return (
    <Card sx={{ height: '120px' }}>
      {/* info icon button top right conditionally rendered */}
      {
        isNullOrUndefined(infoText) === true
          || infoText.length === 0
          ? null
          : (
              <IconButton
                aria-label="info"
                sx={{ float: 'right' }}
                onClick={handleShowInfo}
              >
                <InfoIcon />
              </IconButton>
          )
      }

      <CardContent sx={{ padding: CARD_CONTENT_PADDING_PX }}>
        {
          isMissingMetric === true
            ? (
              <EmptyIndicatorContent
                descriptionText={descriptionText}
                metric={metric}
                footerText={footerText}
              />
            )
            : (
              <IndicatorContent
                descriptionText={descriptionText}
                metric={metric}
                footerText={footerText}
              />
            )
        }
      </CardContent>

      <Dialog
        open={infoState.isInfoDialogVisible}
        onClose={handleHideInfo}
        maxWidth="sm"
      >
        <div style={{ padding: '3em' }}>
          <Typography variant="body1">{infoText}</Typography>
        </div>

        <DialogActions>
          <Button onClick={handleHideInfo}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

Indicator.propTypes = {
  descriptionText: PropTypes.string.isRequired,
  metric: PropTypes.string.isRequired,
  footerText: PropTypes.string.isRequired,
  infoText: PropTypes.string.isRequired
};

export { Indicator };
